// pages/AutomationsPage.js
import React, { useState } from 'react';
import { ContentLayout, PageContainer, ContentSection } from '../components/layout';
import { Loading, ErrorMessage } from '../components/common';
import { AutomationsList } from '../components/automations/AutomationsList';
import { AutomationModal } from '../components/modals/AutomationModal';
import { useAutomations } from '../hooks/useAutomations';
import { HEBREW_TRANSLATIONS } from '../constants/translations';
import { Zap } from 'lucide-react';

export function Automations({ onNavigate }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAutomation, setSelectedAutomation] = useState(null);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'

  const {
    automations,
    loading,
    error,
    createAutomation,
    updateAutomation,
    toggleAutomation,
    deleteAutomation,
    clearError
  } = useAutomations();

  // Handle opening modal for new automation
  const handleNewAutomation = () => {
    setSelectedAutomation(null);
    setModalMode('add');
    setIsModalOpen(true);
  };

  // Handle opening modal for editing automation
  const handleEditAutomation = (automation) => {
    setSelectedAutomation(automation);
    setModalMode('edit');
    setIsModalOpen(true);
  };

  // Handle closing modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedAutomation(null);
    clearError();
  };

  // Handle automation submission (create or update)
  const handleAutomationSubmit = async (automationData) => {
    try {
      let result;
      
      if (modalMode === 'edit' && selectedAutomation) {
        result = await updateAutomation(selectedAutomation.id, automationData);
      } else {
        result = await createAutomation(automationData);
      }

      if (result.success) {
        handleCloseModal();
        return { success: true };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Handle automation toggle
  const handleToggleAutomation = async (automationId) => {
    try {
      await toggleAutomation(automationId);
    } catch (error) {
      console.error('Failed to toggle automation:', error);
    }
  };

  // Handle automation deletion
  const handleDeleteAutomation = async (automationId) => {
    if (window.confirm(HEBREW_TRANSLATIONS.deleteAutomationConfirm)) {
      try {
        await deleteAutomation(automationId);
      } catch (error) {
        console.error('Failed to delete automation:', error);
      }
    }
  };

  if (loading && automations.length === 0) {
    return (
      <ContentLayout>
        <PageContainer>
          <div className="flex items-center justify-center min-h-64">
            <Loading />
          </div>
        </PageContainer>
      </ContentLayout>
    );
  }

  return (
    <ContentLayout
        currentPage="automations"
        onNavigate={onNavigate}
      title={HEBREW_TRANSLATIONS.automationStudio}
      description={HEBREW_TRANSLATIONS.manageAutomatedMessages}
      primaryAction={{
        label: HEBREW_TRANSLATIONS.newAutomation,
        onClick: () => handleNewAutomation(),
        icon: <Zap className="w-4 h-4" />
      }}
    >     
        <PageContainer>

        {/* Error Message */}
        {error && (
          <div className="mb-6">
            <ErrorMessage 
              message={error} 
              onDismiss={clearError}
            />
          </div>
        )}

        {/* Main Content */}
        <ContentSection>
          <AutomationsList
            automations={automations}
            loading={loading}
            onEdit={handleEditAutomation}
            onToggle={handleToggleAutomation}
            onDelete={handleDeleteAutomation}
          />
        </ContentSection>
      </PageContainer>
      {/* Automation Modal */}
      <AutomationModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleAutomationSubmit}
        automation={selectedAutomation}
        mode={modalMode}
      />
    </ContentLayout>
  );
}